import React, { useState, createRef } from 'react'
import { PlusCircleOutlined } from '@ant-design/icons'
import { filterSelectOption } from 'packages/@slerp/antd-utils'
import { Form, Select, Divider } from 'antd'
import { requiredRule } from 'components/Orders/RefundFormRules'
import { Category } from '@slerp/controls'

const { Option } = Select

const CategoryDropdown = ({
  categories,
  categoryLoading,
  handleCreateCategory
}: {
  categories: Category[]
  categoryLoading: boolean
  handleCreateCategory: (searchValue: string) => void | undefined
}) => {
  const createCategoryButton = createRef<HTMLButtonElement>()
  const [showPlaceholder, setShowPlaceholder] = useState(true)
  const [currentSearchValue, setCurrentSearchValue] = useState<string>('')
  const categoryPlaceholder =
    categories.length > 0
      ? 'Type to search or create a category'
      : 'Type to create your first category'

  const showCreateCategoryButton = (
    searchValue: string = '',
    options: { children: string }[]
  ) =>
    searchValue.length > 0 &&
    (options.length === 0 ||
      !options.find(
        (option: { children: string }) => searchValue === option.children
      ))

  const handleSearch = (value: string = '') => {
    setCurrentSearchValue(value)
  }

  const handleAddNewCategory = (searchValue: string = '') => {
    handleCreateCategory(searchValue)
    setCurrentSearchValue('')
  }

  return (
    <Form.Item
      name='product_category_ids'
      rules={requiredRule}
      className='_mb-0'
    >
      <Select
        showSearch
        mode='multiple'
        dropdownClassName='select-multiple'
        placeholder={showPlaceholder ? categoryPlaceholder : ''}
        optionFilterProp='name'
        filterOption={filterSelectOption}
        data-testid='product-form-category'
        notFoundContent={<></>}
        loading={categoryLoading}
        disabled={categoryLoading}
        onFocus={() => setShowPlaceholder(false)}
        onBlur={() => setShowPlaceholder(true)}
        searchValue={currentSearchValue}
        defaultActiveFirstOption={false}
        onInputKeyDown={(e) => {
          // Triggers category creation on enter
          if (e.key === 'Enter') {
            createCategoryButton.current?.click()
          }
        }}
        onSearch={handleSearch}
        dropdownRender={(menu) => {
          return (
            <div
              onMouseDown={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
            >
              <div className='select-option-guide'>
                <p className='text'>{categoryPlaceholder}</p>
              </div>
              {(categories?.length > 0 || currentSearchValue?.length > 0) && (
                <Divider className='_my-0' />
              )}
              {showCreateCategoryButton(
                currentSearchValue,
                categories.map((cat) => ({ children: cat.name }))
              ) && (
                <>
                  <button
                    data-testid='product-form-category-create-button'
                    className='select-option-extra -active'
                    onClick={() => handleAddNewCategory(currentSearchValue)}
                    ref={createCategoryButton}
                  >
                    <div className='icon'>
                      <PlusCircleOutlined />
                    </div>
                    <p className='text'>
                      Create "{currentSearchValue}" category
                    </p>
                    <p className='help'>Press enter</p>
                  </button>
                  {categories.length > 0 && <Divider className='_my-0' />}
                </>
              )}
              <div onClick={() => setCurrentSearchValue('')}>{menu}</div>
            </div>
          )
        }}
      >
        {categories?.map((cat) => {
          return (
            <Option
              key={`dropdown-category-${cat.id}`}
              value={cat.id}
              key-testid={`product-form-category-${cat.id}`}
              data-testid={`product-form-category-${cat.id}`}
            >
              {cat.name}
            </Option>
          )
        })}
      </Select>
    </Form.Item>
  )
}

export default CategoryDropdown
